import { GetServerSidePropsContext } from 'next';
import { ApiClient, CookieOverrides } from '@dozuki/web-js/utils';
import { LAST_CLOSED_WORLD_MESSAGE, SHOULD_USE_GUIDE_STEP_EDIT_V2 } from './cookies';

/**
 * Add any cookie names that we'd like to pass through server side API calls.
 * The session cookie is handled separately since its name is dynamic.
 */
const cookiesToInclude = [LAST_CLOSED_WORLD_MESSAGE, SHOULD_USE_GUIDE_STEP_EDIT_V2];

/**
 * Add any header names that we'd like to pass through server side API calls.
 */
const headersToInclude: { [env in typeof process.env.NODE_ENV]?: string[] } = {
   production: ['x-forwarded-for', 'clientip', 'host'],
};

export function getServerSideApiClient(req: GetServerSidePropsContext['req']): ApiClient {
   const baseUrl = getBaseUrl();
   const cookieOverrides = getRequiredCookies(req);
   const headers = getRequiredHeaders(req);

   return new ApiClient({ baseUrl, cookieOverrides, baseOptions: { headers } });
}

export const getServerSideMonolithClient = getServerSideApiClient;

export type MonolithClient = ApiClient;

function getBaseUrl(): string {
   const url = process.env.SERVER_SIDE_MONOLITH_API_URL;
   if (!url) {
      throw new Error('Missing environment variable SERVER_SIDE_MONOLITH_API_URL.');
   }

   return url;
}

function getRequiredCookies(req: GetServerSidePropsContext['req']): CookieOverrides {
   const cookies: CookieOverrides = {};

   for (const field in req.cookies) {
      const value = req.cookies[field];
      if ((field.includes('session') || cookiesToInclude.includes(field)) && value) {
         cookies[field] = value;
      }
   }

   return cookies;
}

function getRequiredHeaders(req: GetServerSidePropsContext['req']): Record<string, string> {
   return (
      headersToInclude[process.env.NODE_ENV]?.reduce((headers, headerName) => {
         headerName = headerName.toLowerCase();
         const value = req.headers[headerName] as string;
         if (value) {
            headers[headerName] = value;
         }
         return headers;
      }, {} as Record<string, string>) || {}
   );
}

function getServerSideMonolithApiUrl(): string | undefined {
   const remoteDevSiteUrl = process.env.REMOTE_DEV_SITE_URL;
   const serverSideMonolithApiUrl = process.env.SERVER_SIDE_MONOLITH_API_URL;
   if (remoteDevSiteUrl && !serverSideMonolithApiUrl) {
      console.warn(
         'REMOTE_DEV_SITE_URL environment variable is deprecated. Please use SERVER_SIDE_MONOLITH_API_URL.'
      );
   }

   const url = serverSideMonolithApiUrl || remoteDevSiteUrl;
   if (!url && process.env.NODE_ENV === 'development') {
      throw new Error(
         'Missing environment variable SERVER_SIDE_MONOLITH_API_URL. Please add it to .env.local'
      );
   }

   return url;
}
